import React, { useState } from 'react';
import { updateTollgateState } from '../../services/firestoreService';

const COLORS = {
  navy: '#071D39',
  green: '#75BB2E',
  muted: '#516469',
  dim: '#89A8B1',
  border: '#d0d4da',
  lightBg: '#f4f5f7',
};

const Overlay = ({ children }) => (
  <div style={{
    position: 'fixed', inset: 0,
    background: 'rgba(7,29,57,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, backdropFilter: 'blur(3px)',
  }}>
    <div style={{
      background: '#fff', borderRadius: 16,
      width: 'min(480px, 92vw)',
      boxShadow: '0 24px 64px rgba(7,29,57,0.22)',
    }}>
      {children}
    </div>
  </div>
);

const ModalHeader = ({ icon, title, subtitle, bg }) => (
  <div style={{
    background: bg || COLORS.navy,
    borderRadius: '16px 16px 0 0',
    padding: '26px 28px 20px', color: '#fff',
  }}>
    <div style={{ fontSize: 30, marginBottom: 8 }}>{icon}</div>
    <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 5 }}>{title}</div>
    {subtitle && (
      <div style={{ fontSize: 13, opacity: 0.75, lineHeight: 1.5 }}>{subtitle}</div>
    )}
  </div>
);

const Btn = ({ label, onClick, secondary, disabled }) => (
  <button onClick={onClick} disabled={disabled} style={{
    padding: '10px 20px', borderRadius: 9, fontWeight: 600,
    fontSize: 13.5, cursor: disabled ? 'not-allowed' : 'pointer',
    border: secondary ? `1px solid ${COLORS.border}` : 'none',
    background: secondary ? COLORS.lightBg : COLORS.green,
    color: secondary ? COLORS.navy : '#fff',
    opacity: disabled ? 0.6 : 1,
  }}>
    {label}
  </button>
);

const priceChangeTasks = [
  { id: 'pc1', name: 'Complete Change Authorization Form & have Sellers execute' },
  { id: 'pc2', name: 'Update new price/terms on MLS' },
  { id: 'pc3', name: 'Publish price change to all agents via ShowingTime' },
  { id: 'pc4', name: 'Run SM posts each day for 3 days in advance of any OH' },
  { id: 'pc5', name: 'Create Open House in MLS EACH TIME you hold one' },
];

// ── POPUP: Showing Started (fires at 50% MLS) ──────────────────────────────
const ShowingStartedPopup = ({ orgId, projectId, project, onYes, onNo }) => {
  const [saving, setSaving] = useState(false);

  const handleYes = async () => {
    setSaving(true);
    try {
      await updateTollgateState(orgId, projectId, {
        showingStarted: true,
        showingPromptLastAskedAt: new Date().toISOString(),
        showingFeedbackReminderLastAt: new Date().toISOString(),
      });
      onYes();
    } catch (err) {
      console.error('TollgateModal handleYes error:', err);
      alert('Something went wrong saving — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNo = async () => {
    setSaving(true);
    try {
      await updateTollgateState(orgId, projectId, {
        showingStarted: false,
        showingPromptLastAskedAt: new Date().toISOString(),
      });
      onNo();
    } catch (err) {
      console.error('TollgateModal handleNo error:', err);
      alert('Something went wrong saving — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay>
      <ModalHeader
        icon="🏡"
        title="You're halfway through MLS setup!"
        subtitle={`Great progress on ${project.propertyAddress || project.clientName || 'this listing'} — quick check-in before you continue.`}
      />
      <div style={{ padding: '24px 28px 28px' }}>
        <p style={{ margin: '0 0 20px', color: COLORS.muted, fontSize: 14.5, lineHeight: 1.6 }}>
          Have you started showing <strong>{project.propertyAddress || project.clientName || 'the property'}</strong> yet?
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn label={saving ? 'Saving...' : '✅ Yes, we\'re showing!'} onClick={handleYes} disabled={saving} />
          <Btn label="Not yet" secondary onClick={handleNo} disabled={saving} />
        </div>
      </div>
    </Overlay>
  );
};

// ── POPUP: 48hr Re-ask (not showing yet) ──────────────────────────────────
const ShowingReminderPopup = ({ orgId, projectId, onYes, onNo }) => {
  const [saving, setSaving] = useState(false);

  const handleYes = async () => {
    setSaving(true);
    try {
      await updateTollgateState(orgId, projectId, {
        showingStarted: true,
        showingPromptLastAskedAt: new Date().toISOString(),
        showingFeedbackReminderLastAt: new Date().toISOString(),
      });
      onYes();
    } catch (err) {
      console.error('TollgateModal handleYes error:', err);
      alert('Something went wrong saving — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNo = async () => {
    setSaving(true);
    try {
      await updateTollgateState(orgId, projectId, {
        showingStarted: false,
        showingPromptLastAskedAt: new Date().toISOString(),
      });
      onNo();
    } catch (err) {
      console.error('TollgateModal handleNo error:', err);
      alert('Something went wrong saving — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay>
      <ModalHeader
        icon="⏰"
        title="Showing update"
        subtitle="Checking in — it's been 48 hours since we last asked."
        bg="#1a3a5c"
      />
      <div style={{ padding: '24px 28px 28px' }}>
        <p style={{ margin: '0 0 20px', color: COLORS.muted, fontSize: 14.5, lineHeight: 1.6 }}>
          Has the property started <strong>showing yet</strong>?
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn label={saving ? 'Saving...' : '✅ Yes, showings are live!'} onClick={handleYes} disabled={saving} />
          <Btn label="Still not yet" secondary onClick={handleNo} disabled={saving} />
        </div>
      </div>
    </Overlay>
  );
};

// ── POPUP: 72hr Feedback Reminder ─────────────────────────────────────────
const ShowingFeedbackPopup = ({ orgId, projectId, onDismiss }) => {
  const [saving, setSaving] = useState(false);

  const handleDismiss = async () => {
    setSaving(true);
    try {
      await updateTollgateState(orgId, projectId, {
        showingFeedbackReminderLastAt: new Date().toISOString(),
      });
      onDismiss();
    } catch (err) {
      console.error('TollgateModal handleDismiss error:', err);
      alert('Something went wrong saving — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay>
      <ModalHeader
        icon="💬"
        title="Showing feedback reminder"
        subtitle="Don't let this slip — it's been 72 hours!"
      />
      <div style={{ padding: '24px 28px 28px' }}>
        <div style={{
          background: COLORS.lightBg, borderRadius: 10,
          padding: '14px 16px', marginBottom: 18,
          fontSize: 13.5, lineHeight: 1.65,
        }}>
          <div style={{ fontWeight: 700, color: COLORS.navy, marginBottom: 6 }}>
            📋 Two things to action now:
          </div>
          <div style={{ color: COLORS.muted }}>
            1. <strong>Request feedback</strong> from each showing agent & share summary with your Seller
          </div>
          <div style={{ color: COLORS.muted, marginTop: 6 }}>
            2. <strong>Add showing agent info</strong> to your phone & CRM for every showing request
          </div>
        </div>
        <Btn
          label={saving ? 'Saving...' : 'Got it — on it! ✓'}
          onClick={handleDismiss}
          disabled={saving}
        />
      </div>
    </Overlay>
  );
};

// ── POPUP: Monday Price Change Check ──────────────────────────────────────
const PriceChangePopup = ({ orgId, projectId, onYes, onNo }) => {
  const [saving, setSaving] = useState(false);

  const handleNo = async () => {
    setSaving(true);
    try {
      await updateTollgateState(orgId, projectId, {
        priceChangeReminderLastAt: new Date().toISOString(),
      });
      onNo();
    } catch (err) {
      console.error('TollgateModal handleNo error:', err);
      alert('Something went wrong saving — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay>
      <ModalHeader
        icon="📅"
        title="Weekly check-in — Price change?"
        subtitle="Monday morning reminder for this active listing."
        bg="#1a3a5c"
      />
      <div style={{ padding: '24px 28px 28px' }}>
        <p style={{ margin: '0 0 20px', color: COLORS.muted, fontSize: 14.5, lineHeight: 1.6 }}>
          Have there been any <strong>price changes</strong> on this listing this week?
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn label={saving ? 'Saving...' : 'Yes, there was a change'} onClick={onYes} disabled={saving} />
          <Btn label="No changes this week" secondary onClick={handleNo} disabled={saving} />
        </div>
      </div>
    </Overlay>
  );
};

// ── POPUP: Price Change Checklist ─────────────────────────────────────────
const PriceChangeChecklistPopup = ({ orgId, projectId, onDone }) => {
  const [items, setItems] = useState(priceChangeTasks.map(t => ({ ...t, done: false })));
  const [saving, setSaving] = useState(false);
  const allDone = items.every(t => t.done);

  const toggle = (id) => setItems(prev => prev.map(t => t.id === id ? { ...t, done: !t.done } : t));

  const handleDone = async () => {
    setSaving(true);
    try {
      await updateTollgateState(orgId, projectId, {
        priceChangeReminderLastAt: new Date().toISOString(),
      });
      onDone();
    } catch (err) {
      console.error('TollgateModal handleDone error:', err);
      alert('Something went wrong saving — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay>
      <ModalHeader
        icon="✏️"
        title="Price change checklist"
        subtitle="Check off each item as you complete it."
      />
      <div style={{ padding: '24px 28px 28px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
          {items.map(t => (
            <label key={t.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              cursor: 'pointer',
              background: t.done ? '#f0f8e8' : COLORS.lightBg,
              borderRadius: 9, padding: '12px 14px',
              border: `1px solid ${t.done ? '#b8e077' : COLORS.border}`,
            }}>
              <input
                type="checkbox" checked={t.done}
                onChange={() => toggle(t.id)}
                style={{ marginTop: 2, accentColor: COLORS.green, width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{
                fontSize: 13.5, lineHeight: 1.45,
                color: t.done ? COLORS.muted : COLORS.navy,
                textDecoration: t.done ? 'line-through' : 'none',
              }}>{t.name}</span>
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Btn
            label={saving ? 'Saving...' : allDone ? '✅ All done!' : 'Close for now'}
            onClick={handleDone}
            disabled={saving}
          />
          {!allDone && (
            <span style={{ fontSize: 12, color: COLORS.dim }}>
              {items.filter(t => !t.done).length} items remaining
            </span>
          )}
        </div>
      </div>
    </Overlay>
  );
};

// ── MAIN EXPORT ───────────────────────────────────────────────────────────
const TollgateModal = ({ type, orgId, projectId, project, onClose }) => {
  if (type === 'showing-started') {
    return <ShowingStartedPopup orgId={orgId} projectId={projectId} project={project} onYes={onClose} onNo={onClose} />;
  }
  if (type === 'showing-reminder') {
    return <ShowingReminderPopup orgId={orgId} projectId={projectId} onYes={onClose} onNo={onClose} />;
  }
  if (type === 'feedback-reminder') {
    return <ShowingFeedbackPopup orgId={orgId} projectId={projectId} onDismiss={onClose} />;
  }
  if (type === 'price-change') {
    return <PriceChangePopup orgId={orgId} projectId={projectId} onYes={() => onClose('price-checklist')} onNo={onClose} />;
  }
  if (type === 'price-checklist') {
    return <PriceChangeChecklistPopup orgId={orgId} projectId={projectId} onDone={onClose} />;
  }
  return null;
};

export default TollgateModal;